import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import StructuredData from "@/components/StructuredData";
import HeroSection from "@/components/HeroSection";
import SectionHeading from "@/components/SectionHeading";
import FeatureCard from "@/components/FeatureCard";
import CTASection from "@/components/CTASection";
import { Shield, Network, Key, Lock, Database, Coins, Factory, Mail, Eye, Globe2, FileSearch } from "lucide-react";

const Solutions = () => {
  const [solutions, setSolutions] = useState([]);
  const [groupedSolutions, setGroupedSolutions] = useState({});

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/data/solutions.json`)
      .then((res) => res.json())
      .then((data) => {
        setSolutions(data);
        // Group solutions by category
        const grouped = data.reduce((acc, solution) => {
          if (!acc[solution.category]) {
            acc[solution.category] = [];
          }
          acc[solution.category].push(solution);
          return acc;
        }, {});
        setGroupedSolutions(grouped);
      })
      .catch((err) => console.error("Error loading solutions:", err));
  }, []);

  const getCategoryIcon = (category) => {
    if (category.includes("DLP")) return Database; // Data protection / leakage
    if (category.includes("FCRM")) return Coins; // Financial / transaction focus
    if (category.includes("OT")) return Factory; // Industrial / OT environments
    if (category.includes("Email")) return Mail; // Email protection
    if (category.includes("Cyber Intelligence")) return Eye; // Monitoring / visibility
    if (category.includes("Brand")) return Globe2; // Online / web presence
    if (category.includes("Compromise")) return FileSearch; // Investigation / analysis
    if (category.includes("SOC")) return Shield; // Security Operations Center
    if (category.includes("Network")) return Network; // Network security
    if (category.includes("Identity")) return Key; // Identity & access
    return Lock; // Default fallback
  };

  return (
    <>
      <SEO
        title="Cybersecurity Solutions - SOC, PAM, CIAM, Network Security | Gulf Digital"
        description="Comprehensive cybersecurity solutions including 24/7 SOC, Privileged Access Management (PAM), Customer Identity Access Management (CIAM), and network protection for Middle East organizations."
        url="/solutions"
        ogTitle="Cybersecurity Solutions in KSA | SOC, PAM, CIAM | Gulf Digital"
        ogDescription="Explore Gulf Digital's Cybersecurity solutions in KSA, including 24/7 SOC, Privileged Access Management, CIAM, and network protection for Middle East organizations."
        twitterTitle="Cybersecurity Solutions in KSA | Gulf Digital"
        twitterDescription="Enterprise-grade Cybersecurity solutions in KSA covering SOC, PAM, CIAM, and network security, built for Middle East businesses."
      />
      <StructuredData type="Service" data={{
        name: 'Cybersecurity Solutions',
        description: 'Enterprise-grade security solutions including SOC, PAM, CIAM, network security, threat intelligence, and compliance monitoring'
      }} />

      <HeroSection
        eyebrow="Our Solutions"
        title="Enterprise-Grade Cybersecurity Solutions"
        subtitle="Comprehensive security solutions designed to protect your organization from evolving cyber threats. From threat detection to identity management, we've got you covered."
        primaryCTA={{ text: "Contact Sales", href: "/contact" }}
        secondaryCTA={{ text: "View Services", href: "/services" }}
      />

      {/* Solutions by Category */}
      {Object.entries(groupedSolutions).map(
        ([category, categorySolutions], categoryIndex) => {
          const Icon = getCategoryIcon(category);
          return (
            <section
              key={category}
              id={category.toLowerCase().replace(/\s+/g, "-")}
              className={`py-20 ${
                categoryIndex % 2 === 0 ? "bg-white" : "bg-warm-gray-50"
              }`}
              data-testid={`category-section-${category
                .toLowerCase()
                .replace(/\s+/g, "-")}`}
            >
              <div className="container-custom">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="flex items-center space-x-4 mb-12"
                >
                  <div className="w-16 h-16 rounded-xl bg-brand-maroon/10 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-brand-maroon" />
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold">
                      {category}
                    </h2>
                    <p className="text-muted-foreground mt-1">
                      {categorySolutions.length}{" "}
                      {categorySolutions.length === 1
                        ? "solution"
                        : "solutions"}{" "}
                      available
                    </p>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {categorySolutions.map((solution, index) => (
                    <FeatureCard
                      key={solution.id}
                      title={solution.title}
                      description={solution.description}
                      features={solution.features}
                      delay={index * 0.1}
                    />
                  ))}
                </div>
              </div>
            </section>
          );
        }
      )}

      {/* PAM Use Cases Section
      <section className="py-20 bg-brand-maroon" data-testid="pam-use-cases-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="PAM Use Cases"
            title="Privileged Access Management for Every Need"
            description="Our PAM solutions address critical security challenges across your organization."
            descriptionColor='white'
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {[
              'Third-Party Access Control',
              'Audit & Compliance',
              'Zero Trust Implementation',
              'Cyber Insurance Requirements',
              'Insider Threat Mitigation',
              'Operational Efficiency',
              'Fraud Prevention'
            ].map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20"
              >
                <h3 className="text-lg font-semibold text-white">{useCase}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Benefits Section */}
      <section className="py-20 bg-warm-gray-50" data-testid="benefits-section">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Key Benefits"
            title="Why Choose Our Solutions"
            description="Our solutions deliver measurable security and business outcomes."
            titleColor="foreground"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Reduced Risk",
                description:
                  "Minimize security vulnerabilities and threat exposure",
              },
              {
                title: "Compliance Ready",
                description: "Meet regulatory requirements with confidence",
              },
              {
                title: "Cost Effective",
                description: "Optimize security spend with proven ROI",
              },
              {
                title: "Expert Support",
                description: "24/7 access to certified security professionals",
              },
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 rounded-full bg-brand-maroon/10 flex items-center justify-center mx-auto mb-4">
                  <div className="w-6 h-6 rounded-full bg-brand-maroon" />
                </div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready to Enhance Your Security Posture?"
        description="Let our experts help you choose the right solutions for your organization."
      />
    </>
  );
};

export default Solutions;
